/**
 * Improved scripted prompts for the medications review conversation.
 * Voice conversation focuses only on: name, dosage, frequency
 */

// ============================================================================
// TYPES (for TypeScript projects)
// ============================================================================
/**
 * @typedef {Object} Medication
 * @property {string} [name]
 * @property {string} [dosage]
 * @property {string} [frequency]
 * @property {string} [prescribedBy] - Not used in voice flow
 * @property {string} [pharmacy] - Not used in voice flow
 * @property {string} [notes] - Not used in voice flow
 */

/**
 * @typedef {'keep'|'remove'|'edit'|'next'|'add'|'no'|'undo'} UserAction
 */

/**
 * @typedef {'name'|'dosage'|'frequency'} SectionKey
 */

// ============================================================================
// CONFIGURATION
// ============================================================================
export const CONFIG = {
  requiredFields: ['name', 'dosage', 'frequency'],
  confirmationDelay: 500, // ms before moving to next medication
};

/** Section key to user-facing label. Voice uses name/dosage/frequency; edit/add optional also use prescribedBy, pharmacy, notes. */
export const SECTION_LABELS = {
  name: 'name',
  dosage: 'dosage',
  frequency: 'frequency',
  prescribedBy: 'prescribed by',
  pharmacy: 'pharmacy',
  notes: 'notes',
};

/** Synonyms for better voice recognition */
const SECTION_SYNONYMS = {
  name: ['name', 'medication', 'drug', 'med', 'medicine'],
  dosage: ['dosage', 'dose', 'amount', 'strength', 'milligrams', 'mg'],
  frequency: ['frequency', 'how often', 'schedule', 'timing', 'when'],
  prescribedBy: ['prescribed by', 'prescriber', 'doctor', 'who prescribed'],
  pharmacy: ['pharmacy', 'where to get'],
  notes: ['notes', 'instructions', 'notes section'],
};

// ============================================================================
// MAIN CONVERSATION PROMPTS
// ============================================================================

export const INTRO_HAVE_MEDS = (n) =>
  n > 0
    ? `Let's review your ${n} medication${n === 1 ? '' : 's'}. If they're all correct, say "all correct" or "verified" and we'll move on. Or I'll name each one — keep, remove, or edit are your choices.`
    : "You don't have any medications listed yet. Say add to add one, or skip to move on.";

export const INTRO_NO_MEDS = () =>
  "You don't have any medications listed. Would you like to add any? Say add or no.";

export const MEDICATION_PROMPT = (med, index = 0, total = 1) => {
  const name = med?.name || 'Unnamed medication';
  const dosage = med?.dosage ? `, ${med.dosage}` : '';
  const freq = med?.frequency ? `, ${med.frequency}` : '';
  const position = total > 1 ? `Medication ${index + 1} of ${total}: ` : '';
  return `${position}${name}${dosage}${freq}. Keep, remove, or edit are your choices.`;
};

export const KEPT_CONFIRMATION = (medName) => 
  `Keeping ${medName || 'that medication'}.`;

export const REMOVED_CONFIRMATION = (medName) => 
  `Removed ${medName || 'that medication'}.`;

// ============================================================================
// EDIT FLOW PROMPTS
// ============================================================================

/** Prompt when entering voice-driven edit: which section to edit. */
export const EDIT_WHICH_SECTION = () =>
  "Say the name of any section you want to edit, or say done to confirm.";

/** Ask for the new value for a section. */
export const EDIT_WHAT_SECTION_VALUE = (sectionLabel) =>
  `What should the ${sectionLabel} be?`;

/** After setting a section, prompt for next section to edit. */
export const EDIT_WHICH_SECTION_NEXT = () =>
  "Say the name of any section you want to edit, or say done to confirm.";

/** Confirm a section was set. */
export const EDIT_SECTION_SET = (sectionLabel, value) =>
  `Got it. ${sectionLabel} is now ${value}.`;

/** Required sections are set; offer to move on or edit another section. */
export const EDIT_READY_TO_MOVE_ON = () =>
  "Name, dosage, and frequency are set. Say the name of any section you want to edit, or say done to confirm.";

/** Read back updated medication and ask for confirmation. */
export const EDIT_CONFIRM_PROMPT = (med) => {
  const name = med?.name || 'Unnamed';
  const parts = [name];
  if (med?.dosage) parts.push(med.dosage);
  if (med?.frequency) parts.push(med.frequency);
  return `I've updated it to ${parts.join(', ')}. Is that correct?`;
};

export const EDIT_AGAIN_PROMPT = () =>
  "No problem. Say the name of any section you want to edit, or say done to confirm.";

// ============================================================================
// ADD FLOW PROMPTS
// ============================================================================

export const ADD_PROMPT = () =>
  "Any new medications to add? Say add or no.";

/** When user said "all correct" / "verified" and we skipped per-med review. */
export const ALL_VERIFIED_PROMPT = () =>
  "All verified. Any new medications to add? Say add or no.";

/** Voice-led add: ask for medication name first. */
export const ADD_ASK_NAME = () =>
  "What's the medication name?";

/** Voice-led add: ask for dosage. */
export const ADD_ASK_DOSAGE = () =>
  "What's the dosage?";

/** Voice-led add: ask for frequency. */
export const ADD_ASK_FREQUENCY = () =>
  "How often do you take it?";

/** Voice-led add: required fields set; user can name any section to edit or say done to save. */
export const ADD_OPTIONAL_PROMPT = () =>
  "Say the name of any section you want to edit, or say done to save.";

/** Before saving: confirm add. */
export const ADD_CONFIRM_PROMPT = (draft) => {
  const parts = [draft?.name || 'Unnamed', draft?.dosage, draft?.frequency].filter(Boolean);
  return `I'll add ${parts.join(', ')}. Should I save it?`;
};

/** After saving a new medication by voice. */
export const ADD_SAVED = (medName) =>
  `I've added ${medName || 'that medication'}. Any more to add? Say add or no.`;

export const DONE_PROMPT = () =>
  "We're done reviewing your medications. You can close this when ready.";

/** After all sections (medications + optional allergies, pmh, psh, other) are done. */
export const DONE_PROMPT_FINAL = () =>
  "We're done reviewing your records. You can close this when ready.";

/** Offer to review next CMR section. Order: Allergies → Past Medical History → Past Surgical History → Other history. */
export const OFFER_SECTION_PROMPT = (sectionName) =>
  `Would you like to review ${sectionName} next?`;

/** Section names offered after medications, in order. */
export const SECTION_OFFER_NAMES = ['Allergies', 'Past Medical History', 'Past Surgical History', 'Other history'];

/** Intro when starting allergies review. */
export const ALLERGIES_INTRO = (n) =>
  n > 0
    ? `Let's review your ${n} allergy${n === 1 ? '' : 'ies'}. I'll name each one — keep, remove, or edit are your choices.`
    : "You don't have any allergies listed yet. Say add to add one, or no to move on.";

/** Per-item prompt for allergies. */
export const ALLERGY_ITEM_PROMPT = (item, index = 0, total = 1) => {
  const name = item?.allergen || 'Unknown allergen';
  const type = item?.type ? ` (${item.type})` : '';
  const reaction = item?.reaction ? `, reaction: ${item.reaction}` : '';
  const position = total > 1 ? `Allergy ${index + 1} of ${total}: ` : '';
  return `${position}${name}${type}${reaction}. Keep, remove, or edit are your choices.`;
};

/** Intro when starting PMH review. */
export const PMH_INTRO = (n) =>
  n > 0
    ? `Let's review your past medical history. You have ${n} condition${n === 1 ? '' : 's'}. I'll name each one — keep, remove, or edit are your choices.`
    : "You don't have any past medical history listed yet. Say add to add one, or no to move on.";

/** Per-item prompt for PMH. */
export const PMH_ITEM_PROMPT = (item, index = 0, total = 1) => {
  const condition = item?.condition || 'Unknown condition';
  const status = item?.status ? `, ${item.status}` : '';
  const position = total > 1 ? `Condition ${index + 1} of ${total}: ` : '';
  return `${position}${condition}${status}. Keep, remove, or edit are your choices.`;
};

/** Intro when starting PSH review. */
export const PSH_INTRO = (n) =>
  n > 0
    ? `Let's review your past surgical history. You have ${n} procedure${n === 1 ? '' : 's'}. I'll name each one — keep, remove, or edit are your choices.`
    : "You don't have any past surgical history listed yet. Say add to add one, or no to move on.";

/** Per-item prompt for PSH. */
export const PSH_ITEM_PROMPT = (item, index = 0, total = 1) => {
  const procedure = item?.procedure || 'Unknown procedure';
  const year = item?.year ? `, ${item.year}` : '';
  const position = total > 1 ? `Surgery ${index + 1} of ${total}: ` : '';
  return `${position}${procedure}${year}. Keep, remove, or edit are your choices.`;
};

/** Intro when starting Other history. */
export const OTHER_INTRO = () =>
  "Let's review your other history. Which field do you want to review or update: smoking, alcohol, family history, or notes? Say the field name, or say done to move on.";

/** Other history: voice field key -> storage key on healthRecords.other */
export const OTHER_FIELD_TO_KEY = {
  smoking: 'smokingStatus',
  alcohol: 'alcoholUse',
  family_history: 'familyHistory',
  notes: 'additionalNotes',
};

/** Three suggested values per voice field. User can also say "something else" for freeform. */
export const OTHER_FIELD_OPTIONS = {
  smoking: ['None', 'Former smoker', 'Current smoker'],
  alcohol: ['None', 'Occasional', 'Regular'],
  family_history: ['None', 'Heart disease in family', 'Cancer in family'],
  notes: ['Keep current', 'Add something', 'Clear'],
};

export const OTHER_WHICH_FIELD = () =>
  "Which field do you want to review or update: smoking, alcohol, family history, or notes? Say the field name, or say done to move on.";

export const OTHER_SUGGEST_OPTIONS = (fieldLabel, options) =>
  `For ${fieldLabel}, say: ${options[0]}, ${options[1]}, or ${options[2]}. Or say something else.`;

export const OTHER_ASK_CUSTOM = (fieldLabel) =>
  `What would you like ${fieldLabel} to be?`;

export const OTHER_CONFIRM_FIELD = (fieldLabel, value) =>
  `I'll set ${fieldLabel} to ${value}. Is that correct?`;

export const OTHER_FIELD_SAVED = (fieldLabel) =>
  `Done. ${fieldLabel} is updated. Which field next, or say done to move on.`;

/** Parse reply to one of the four Other voice fields. */
export function parseOtherFieldName(text) {
  if (!text || typeof text !== 'string') return null;
  const t = text.trim().toLowerCase();
  if (/\b(smoking|smoker|tobacco)\b/.test(t)) return 'smoking';
  if (/\b(alcohol|drink|drinking)\b/.test(t)) return 'alcohol';
  if (/\b(family|family history)\b/.test(t)) return 'family_history';
  if (/\b(notes?|note|additional)\b/.test(t)) return 'notes';
  if (/\b(done|finish|move on|skip|continue)\b/.test(t)) return 'done';
  return null;
}

/** Map spoken option to value; returns '__custom__' if "something else", or the option string if match. */
export function parseOtherFieldOption(text, fieldKey, options) {
  if (!text || !options || options.length < 3) return null;
  const t = text.trim().toLowerCase();
  if (/\b(something else|something different|other|different|custom)\b/.test(t)) return '__custom__';
  for (let i = 0; i < options.length; i++) {
    const opt = options[i].toLowerCase();
    if (t.includes(opt) || opt.includes(t) || (opt.split(' ')[0] && t.includes(opt.split(' ')[0]))) return options[i];
  }
  return null;
}

// ============================================================================
// ALLERGY SECTION (review / edit / add)
// ============================================================================

export const ALLERGY_SECTION_LABELS = {
  allergen: 'allergen',
  type: 'type',
  reaction: 'reaction',
  notes: 'notes',
};

export const ADD_ALLERGY_PROMPT = () =>
  "Any allergies to add? Say add or no.";

export const ADD_ALLERGY_ASK_ALLERGEN = () => "What is the allergen?";
export const ADD_ALLERGY_ASK_TYPE = () => "What type: drug, food, environmental, or other?";
export const ADD_ALLERGY_ASK_REACTION = () => "What reaction do you have?";
export const ADD_ALLERGY_OPTIONAL = () =>
  "Say the name of any section you want to add, or say done to save.";
export const ADD_ALLERGY_CONFIRM = (draft) =>
  `I'll add allergy to ${draft?.allergen || 'unknown'}. Should I save it?`;
export const ADD_ALLERGY_SAVED = (name) =>
  `I've added ${name || 'that allergy'}. Any more to add? Say add or no.`;

/** Parse which allergy field the user wants to edit. */
export function parseAllergySectionName(text) {
  if (!text || typeof text !== 'string') return null;
  const t = text.trim().toLowerCase();
  if (/\b(allergen|allergy|what)\b/.test(t)) return 'allergen';
  if (/\b(type|kind|category)\b/.test(t)) return 'type';
  if (/\b(reaction|react|symptom)\b/.test(t)) return 'reaction';
  if (/\b(notes?|note|other)\b/.test(t)) return 'notes';
  return null;
}

/** Read back edited allergy and ask for confirmation. */
export const EDIT_ALLERGY_CONFIRM_PROMPT = (item) => {
  const a = item?.allergen || 'Unknown';
  const t = item?.type ? `, ${item.type}` : '';
  const r = item?.reaction ? `, reaction: ${item.reaction}` : '';
  return `I've updated it to ${a}${t}${r}. Is that correct?`;
};

// ============================================================================
// PMH SECTION (review / edit / add)
// ============================================================================

export const PMH_SECTION_LABELS = { condition: 'condition', status: 'status', notes: 'notes' };

export const ADD_PMH_PROMPT = () => "Any past medical conditions to add? Say add or no.";
export const ADD_PMH_ASK_CONDITION = () => "What is the condition?";
export const ADD_PMH_ASK_STATUS = () => "Status: active, controlled, or resolved?";
export const ADD_PMH_OPTIONAL = () => "Say the name of any section you want to add, or say done to save.";
export const ADD_PMH_CONFIRM = (draft) =>
  `I'll add ${draft?.condition || 'unknown condition'}. Should I save it?`;
export const ADD_PMH_SAVED = (name) =>
  `I've added ${name || 'that condition'}. Any more to add? Say add or no.`;
export const EDIT_PMH_CONFIRM_PROMPT = (item) => {
  const c = item?.condition || 'Unknown';
  const s = item?.status ? `, ${item.status}` : '';
  return `I've updated it to ${c}${s}. Is that correct?`;
};

export function parsePmhSectionName(text) {
  if (!text || typeof text !== 'string') return null;
  const t = text.trim().toLowerCase();
  if (/\b(condition|diagnosis|disease)\b/.test(t)) return 'condition';
  if (/\b(status|active|controlled|resolved)\b/.test(t)) return 'status';
  if (/\b(notes?|note|other)\b/.test(t)) return 'notes';
  return null;
}

// ============================================================================
// PSH SECTION (review / edit / add)
// ============================================================================

export const PSH_SECTION_LABELS = {
  procedure: 'procedure',
  year: 'year',
  hospital: 'hospital',
  surgeon: 'surgeon',
  notes: 'notes',
};

export const ADD_PSH_PROMPT = () => "Any past surgeries or procedures to add? Say add or no.";
export const ADD_PSH_ASK_PROCEDURE = () => "What is the procedure or surgery?";
export const ADD_PSH_ASK_YEAR = () => "What year?";
export const ADD_PSH_OPTIONAL = () => "Say the name of any section you want to add, or say done to save.";
export const ADD_PSH_CONFIRM = (draft) =>
  `I'll add ${draft?.procedure || 'unknown procedure'}. Should I save it?`;
export const ADD_PSH_SAVED = (name) =>
  `I've added ${name || 'that procedure'}. Any more to add? Say add or no.`;
export const EDIT_PSH_CONFIRM_PROMPT = (item) => {
  const p = item?.procedure || 'Unknown';
  const y = item?.year ? `, ${item.year}` : '';
  return `I've updated it to ${p}${y}. Is that correct?`;
};

export function parsePshSectionName(text) {
  if (!text || typeof text !== 'string') return null;
  const t = text.trim().toLowerCase();
  if (/\b(procedure|surgery|surgical)\b/.test(t)) return 'procedure';
  if (/\b(year|when|date)\b/.test(t)) return 'year';
  if (/\b(hospital|where)\b/.test(t)) return 'hospital';
  if (/\b(surgeon|doctor|who)\b/.test(t)) return 'surgeon';
  if (/\b(notes?|note|other)\b/.test(t)) return 'notes';
  return null;
}

// ============================================================================
// PROGRESS & FEEDBACK PROMPTS
// ============================================================================

export const PROGRESS_PROMPT = (current, total) =>
  `That's ${current} of ${total} medications reviewed.`;

export const ALMOST_DONE_PROMPT = (remaining) =>
  remaining === 1 
    ? "Just one more medication to review." 
    : `${remaining} medications left to review.`;

export const CLARIFICATION_PROMPT = (possibleActions) =>
  `I didn't catch that. You can say: ${possibleActions.join(', ')}.`;

export const INVALID_SECTION_PROMPT = () =>
  "I didn't recognize that section. Say the name of any section you want to edit, or say done to confirm.";

export const UNDO_PROMPT = () => 
  "Okay, let me undo that. What would you like to do instead?";

export const VALIDATION_ERROR_PROMPT = (errors) =>
  `Missing required information: ${errors.join(', ')}. Let's fill those in.`;

// ============================================================================
// PARSING FUNCTIONS
// ============================================================================

/**
 * Parse user reply for yes / no (e.g. offer next section).
 * @param {string} text
 * @returns {'yes'|'no'|null}
 */
export function parseYesNo(text) {
  if (!text || typeof text !== 'string') return null;
  const t = text.trim().toLowerCase();
  if (/\b(yes|yeah|yep|sure|ok|okay|yup|correct|right)\b/.test(t)) return 'yes';
  if (/\b(no|nope|nah|skip|pass)\b/.test(t)) return 'no';
  return null;
}

/**
 * Parse user reply for keep / remove / edit / next / add / no / undo.
 * Fixed to avoid conflicts and prioritize specific matches.
 * @param {string} text
 * @returns {UserAction|null}
 */
export function parseMedicationReply(text) {
  if (!text || typeof text !== 'string') return null;
  const t = text.trim().toLowerCase();
  
  // Check for undo first (highest priority for corrections)
  if (/\b(undo|oops|wait|actually|mistake|wrong)\b/.test(t)) return 'undo';
  
  // "All correct" / "verified" / "all good" - skip to "any to add?" without reviewing each med
  if (/\b(all\s+correct|all\s+are\s+correct|they'?re\s+all\s+correct|verified|all\s+good|all\s+right|keep\s+all|all\s+of\s+them|all\s+the\s+same)\b/.test(t)) {
    return 'all_keep';
  }
  
  // Order by specificity to avoid conflicts
  if (/\b(remove|delete|stop|drop|discontinue)\b/.test(t) && !/\bdon't\s+remove\b/.test(t)) {
    return 'remove';
  }
  if (/\b(edit|change|modify|update|fix)\b/.test(t)) {
    return 'edit';
  }
  // Confirm/save intent before "add" so "save" and "add it" are not parsed as starting a new add
  if (/\b(yes|yeah|yep|yup|sure|ok|okay|correct|right)\b/.test(t)) return 'keep';
  if (/\b(save|confirm|add it|do it)\b/.test(t)) return 'keep';
  if (/\b(keep|same|good)\b/.test(t)) return 'keep';
  if (/\b(add|new)\b/.test(t) && !/\b(don't|no)\s+add\b/.test(t)) {
    return 'add';
  }
  if (/\b(next|skip|continue|move on)\b/.test(t)) {
    return 'next';
  }
  // "no" but exclude phrases like "no longer"
  if (/\b(no|nope|nah|none|finish)\b/.test(t) && !/\bno\s+(longer|more)\b/.test(t)) {
    return 'no';
  }
  
  return null;
}

/**
 * Parse voice input to a section key (name, dosage, frequency only).
 * Improved to handle synonyms and multi-word phrases.
 * @param {string} text
 * @returns {SectionKey|null}
 */
export function parseSectionName(text) {
  if (!text || typeof text !== 'string') return null;
  const t = text.trim().toLowerCase().replace(/[^\w\s]/g, '');
  
  // Check each section's synonyms
  for (const [section, synonyms] of Object.entries(SECTION_SYNONYMS)) {
    for (const synonym of synonyms) {
      // Match as whole words or within common phrases
      const pattern = new RegExp(`\\b${synonym}\\b`, 'i');
      if (pattern.test(t)) {
        return section;
      }
    }
  }
  
  return null;
}

/**
 * Parse "done editing" / "ready to move on" in edit flow.
 * @param {string} text
 * @returns {boolean}
 */
export function parseReadyToMoveOn(text) {
  if (!text || typeof text !== 'string') return false;
  const t = text.trim().toLowerCase();
  return /\b(done|finished|that's all|ready|move on|nothing else|no more|complete|save)\b/.test(t);
}

/**
 * Detect if input is likely an echo from TTS reading the prompt.
 * Improved to check against the actual last prompt.
 * @param {string} reply
 * @param {string} [lastPrompt] - The last prompt that was spoken
 * @returns {boolean}
 */
export function isLikelyEcho(reply, lastPrompt = '') {
  if (!reply || typeof reply !== 'string') return false;
  const cleanReply = reply.trim().toLowerCase();
  
  // Very short replies are unlikely to be echoes
  if (cleanReply.length < 8) return false;
  
  // Check if reply is a substantial substring of the prompt
  if (lastPrompt) {
    const cleanPrompt = lastPrompt.trim().toLowerCase();
    if (cleanPrompt.includes(cleanReply) && cleanReply.length > 15) {
      return true;
    }
  }
  
  // Common TTS echo patterns
  const echoPatterns = [
    /^\d+\s+or\s+say/,  // "1 or say no to finish"
    /say\s+(add|no|keep|remove|edit)\s+to/,
    /you can say/i,
    /or say no to finish/i,
    /say add to add one/i,
  ];
  
  return echoPatterns.some(pattern => pattern.test(cleanReply));
}

/**
 * Alias for conversation: detect if reply is likely TTS echo of ADD_PROMPT (single-arg, no lastPrompt).
 * @param {string} reply
 * @returns {boolean}
 */
export function isLikelyEchoOfAddPrompt(reply) {
  return isLikelyEcho(reply, '');
}

/**
 * Check for undo/correction intent.
 * @param {string} text
 * @returns {boolean}
 */
export function parseUndoIntent(text) {
  if (!text || typeof text !== 'string') return false;
  const t = text.trim().toLowerCase();
  return /\b(undo|oops|wait|actually|mistake|wrong|go back)\b/.test(t);
}

// ============================================================================
// VALIDATION
// ============================================================================

/**
 * Validate that a medication has all required fields.
 * @param {Medication} med
 * @returns {{isValid: boolean, errors: string[]}}
 */
export function validateMedication(med) {
  const errors = [];
  
  if (!med.name?.trim()) {
    errors.push('name');
  }
  if (!med.dosage?.trim()) {
    errors.push('dosage');
  }
  if (!med.frequency?.trim()) {
    errors.push('frequency');
  }
  
  return { 
    isValid: errors.length === 0, 
    errors 
  };
}

/**
 * Get a list of missing required fields.
 * @param {Medication} med
 * @returns {string[]}
 */
export function getMissingFields(med) {
  const missing = [];
  
  if (!med.name?.trim()) missing.push('name');
  if (!med.dosage?.trim()) missing.push('dosage');
  if (!med.frequency?.trim()) missing.push('frequency');
  
  return missing;
}

/**
 * Get prompt for the next missing required field.
 * @param {Medication} med
 * @returns {string|null}
 */
export function getNextRequiredFieldPrompt(med) {
  const missing = getMissingFields(med);
  
  if (missing.length === 0) return null;
  
  const field = missing[0];
  switch (field) {
    case 'name':
      return ADD_ASK_NAME();
    case 'dosage':
      return ADD_ASK_DOSAGE();
    case 'frequency':
      return ADD_ASK_FREQUENCY();
    default:
      return null;
  }
}

// ============================================================================
// UTILITY / LOGGING
// ============================================================================

/**
 * Log user actions for analytics (hook for your analytics system).
 * @param {UserAction} action
 * @param {Object} context
 */
export function logUserAction(action, context = {}) {
  if (process.env.NODE_ENV === 'development') {
  }
  // Add your analytics call here
}

/**
 * Log parsing failures for debugging.
 * @param {string} input
 * @param {string[]} expectedActions
 */
export function logParsingFailure(input, expectedActions = []) {
  if (process.env.NODE_ENV === 'development') {
    console.warn('[Parse Failed]', { 
      input, 
      expectedActions,
      timestamp: new Date().toISOString() 
    });
  }
}

/**
 * Get a user-friendly list of expected actions for clarification prompts.
 * @param {string} context - 'review' | 'edit' | 'add'
 * @returns {string[]}
 */
export function getExpectedActions(context) {
  switch (context) {
    case 'review':
      return ['keep', 'remove', 'edit', 'next'];
    case 'edit':
      return ['name', 'dosage', 'frequency', 'done'];
    case 'add':
      return ['add', 'no'];
    default:
      return ['keep', 'remove', 'edit', 'add', 'no'];
  }
}

// ============================================================================
// EXPORTS
// ============================================================================

export default {
  // Prompts
  INTRO_HAVE_MEDS,
  INTRO_NO_MEDS,
  MEDICATION_PROMPT,
  KEPT_CONFIRMATION,
  REMOVED_CONFIRMATION,
  EDIT_WHICH_SECTION,
  EDIT_WHAT_SECTION_VALUE,
  EDIT_WHICH_SECTION_NEXT,
  EDIT_SECTION_SET,
  EDIT_READY_TO_MOVE_ON,
  EDIT_CONFIRM_PROMPT,
  EDIT_AGAIN_PROMPT,
  ADD_PROMPT,
  ADD_ASK_NAME,
  ADD_ASK_DOSAGE,
  ADD_ASK_FREQUENCY,
  ADD_OPTIONAL_PROMPT,
  ADD_SAVED,
  DONE_PROMPT,
  DONE_PROMPT_FINAL,
  OFFER_SECTION_PROMPT,
  SECTION_OFFER_NAMES,
  ALLERGIES_INTRO,
  ALLERGY_ITEM_PROMPT,
  PMH_INTRO,
  PMH_ITEM_PROMPT,
  PSH_INTRO,
  PSH_ITEM_PROMPT,
  OTHER_INTRO,
  PROGRESS_PROMPT,
  ALMOST_DONE_PROMPT,
  CLARIFICATION_PROMPT,
  INVALID_SECTION_PROMPT,
  UNDO_PROMPT,
  VALIDATION_ERROR_PROMPT,
  
  // Parsing
  parseMedicationReply,
  parseYesNo,
  parseSectionName,
  parseReadyToMoveOn,
  isLikelyEcho,
  isLikelyEchoOfAddPrompt,
  parseUndoIntent,
  
  // Validation
  validateMedication,
  getMissingFields,
  getNextRequiredFieldPrompt,
  
  // Utilities
  logUserAction,
  logParsingFailure,
  getExpectedActions,
  
  // Constants
  CONFIG,
  SECTION_LABELS,
};